'use client';

import React, { useState } from 'react';

const features = [
    {
        number: '01',
        title: 'Answer a few questions about your idea',
        description: 'Tell us about the problem you solve, who it\'s for, and what makes it unique. Our AI wizard guides you through 5-6 simple questions—no copywriting skills needed. Takes about 2 minutes.',
        visual: 'questions',
    },
    {
        number: '02',
        title: 'AI generates your landing page',
        description: 'Watch your validation page come to life. We create compelling headlines, benefit-focused copy, a professional layout, and an email capture form—all tailored to your idea.',
        visual: 'generate',
    },
    {
        number: '03',
        title: 'Customize to match your brand',
        description: 'Make it yours. Change colors, swap fonts, rearrange sections, edit any text. Drag and drop to reorder. Or just leave it as is—it\'s already designed to convert.',
        visual: 'customize',
    },
    {
        number: '04',
        title: 'Go live with one click',
        description: 'Publish to your own URL (yourproject.waitlistly.live) or connect a custom domain. Your page is live instantly—share it on Twitter, ProductHunt, or wherever your audience hangs out.',
        visual: 'publish',
    },
    {
        number: '05',
        title: 'Track results and collect leads',
        description: 'Watch the signups roll in. Track views, conversion rates, and export your email list anytime. Real numbers that tell you whether to keep building—or pivot to the next idea.',
        visual: 'track',
    },
    {
        number: '06',
        title: 'Engage subscribers with built-in emails',
        description: 'Automatically send branded welcome emails the moment someone joins your waitlist — complete with their queue position and referral link. Get notified on every signup, and when you\'re ready to launch, blast a one-click announcement to your entire waitlist.',
        visual: 'email',
    },
    {
        number: '07',
        title: 'Connect with your favorite tools',
        description: 'Send your leads anywhere with instant webhooks. Connect to Zapier, Make, n8n, Slack, or your custom backend to automate workflows beyond email.',
        visual: 'webhook',
    },
];

export default function FeaturesSection() {
    const [activeIndex, setActiveIndex] = useState(0);

    return (
        <section id="features" className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900">
                        How <span className="text-violet-600">Waitlistly</span> works
                    </h2>
                    <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto">
                        From idea to live validation page in about 5 minutes. No coding, no design skills, no complicated setup.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    {/* Left: Visual Preview */}
                    <div className="relative order-2 lg:order-1">
                        <div className="sticky top-24">
                            <FeatureVisual activeVisual={features[activeIndex].visual} />
                        </div>
                    </div>

                    {/* Right: Accordion */}
                    <div className="space-y-4 order-1 lg:order-2">
                        {features.map((feature, index) => (
                            <FeatureAccordionItem
                                key={feature.number}
                                {...feature}
                                isActive={activeIndex === index}
                                onClick={() => setActiveIndex(index)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

interface FeatureAccordionItemProps {
    number: string;
    title: string;
    description: string;
    isActive: boolean;
    onClick: () => void;
}

function FeatureAccordionItem({ number, title, description, isActive, onClick }: FeatureAccordionItemProps) {
    return (
        <div
            className={`border rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer ${isActive ? 'border-violet-200 bg-violet-50/50 shadow-lg shadow-violet-100/50' : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
            onClick={onClick}
        >
            <div className="flex items-start gap-4 p-6">
                <span className={`text-lg font-bold ${isActive ? 'text-violet-600' : 'text-gray-400'}`}>
                    {number}
                </span>
                <div className="flex-1">
                    <div className="flex items-center justify-between">
                        <h3 className={`text-xl font-semibold ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                            {title}
                        </h3>
                        <svg
                            className={`w-6 h-6 transition-transform duration-300 ${isActive ? 'rotate-180 text-violet-600' : 'text-gray-400'}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                    <div
                        className={`overflow-hidden transition-all duration-300 ${isActive ? 'max-h-40 opacity-100 mt-4' : 'max-h-0 opacity-0'
                            }`}
                    >
                        <p className="text-gray-600 leading-relaxed">{description}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FeatureVisual({ activeVisual }: { activeVisual: string }) {
    const visuals: Record<string, React.ReactNode> = {
        questions: <QuestionsVisual />,
        generate: <GenerateVisual />,
        customize: <CustomizeVisual />,
        publish: <PublishVisual />,
        track: <TrackVisual />,
        email: <EmailVisual />,
        webhook: <WebhookVisual />,
    };

    return (
        <div className="bg-gradient-to-br from-violet-100 to-violet-50 rounded-3xl p-8 min-h-[400px] flex items-center justify-center transition-all duration-500">
            {visuals[activeVisual]}
        </div>
    );
}

function QuestionsVisual() {
    return (
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-violet-600 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                    </svg>
                </div>
                <span className="font-semibold text-gray-900">AI Wizard</span>
            </div>
            <div className="space-y-4">
                <div className="bg-violet-50 rounded-xl p-4">
                    <p className="text-sm text-gray-600 mb-3">What problem does your product solve?</p>
                    <div className="h-10 bg-white rounded-lg border border-gray-200 flex items-center px-3">
                        <span className="text-gray-400 text-sm">Type your answer...</span>
                        <span className="ml-auto w-2 h-5 bg-violet-600 animate-pulse rounded-sm"></span>
                    </div>
                </div>
                <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-violet-600"></div>
                    <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                    <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                    <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                    <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                </div>
            </div>
        </div>
    );
}

function GenerateVisual() {
    return (
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
                <span className="font-semibold text-gray-900">Generating page...</span>
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-violet-600 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-violet-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-violet-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
            </div>
            <div className="space-y-3">
                <div className="h-4 bg-violet-200 rounded-full w-3/4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded-full w-full"></div>
                <div className="h-4 bg-gray-200 rounded-full w-5/6"></div>
                <div className="h-10 bg-violet-600 rounded-xl mt-4 animate-pulse"></div>
                <div className="grid grid-cols-3 gap-2 mt-4">
                    <div className="h-16 bg-violet-50 rounded-lg"></div>
                    <div className="h-16 bg-violet-50 rounded-lg"></div>
                    <div className="h-16 bg-violet-50 rounded-lg"></div>
                </div>
            </div>
        </div>
    );
}

function CustomizeVisual() {
    return (
        <div className="w-full max-w-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 flex items-center gap-2">
                    <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                </div>
                <div className="p-4">
                    <div className="flex gap-4">
                        <div className="flex-1 space-y-2">
                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                            <div className="h-6 bg-gray-300 rounded w-3/4"></div>
                            <div className="h-3 bg-gray-100 rounded w-full"></div>
                        </div>
                        <div className="w-24 space-y-2">
                            <div className="text-xs text-gray-500 mb-2">Colors</div>
                            <div className="flex gap-2">
                                <div className="w-6 h-6 rounded-full bg-violet-600 ring-2 ring-violet-300"></div>
                                <div className="w-6 h-6 rounded-full bg-blue-600"></div>
                                <div className="w-6 h-6 rounded-full bg-emerald-600"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mt-4 flex gap-2 justify-center">
                <div className="px-3 py-1.5 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">Drag sections</div>
                <div className="px-3 py-1.5 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">Edit text</div>
            </div>
        </div>
    );
}

function PublishVisual() {
    return (
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6 animate-fade-in">
            <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">You're live!</h3>
                <div className="bg-gray-100 rounded-xl p-3 mb-4">
                    <span className="text-violet-600 font-mono text-sm">myproject.waitlistly.live</span>
                </div>
                <div className="flex gap-2 justify-center">
                    <button className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-xl">Visit Page</button>
                    <button className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl">Copy Link</button>
                </div>
            </div>
        </div>
    );
}

function TrackVisual() {
    return (
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
                <span className="font-semibold text-gray-900">Analytics</span>
                <span className="text-xs text-gray-500">Last 7 days</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-violet-50 rounded-xl p-3">
                    <div className="text-2xl font-bold text-violet-700">847</div>
                    <div className="text-sm text-gray-600">Views</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3">
                    <div className="text-2xl font-bold text-emerald-700">142</div>
                    <div className="text-sm text-gray-600">Signups</div>
                </div>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-amber-600">16.8%</div>
                <div className="text-sm text-gray-600">Conversion Rate</div>
                <div className="text-xs text-emerald-600 mt-1">Strong demand signal</div>
            </div>
        </div>
    );
}

function EmailVisual() {
    return (
        <div className="w-full max-w-sm animate-fade-in space-y-3">
            {/* Welcome Email Preview */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="bg-violet-600 px-5 py-3 flex items-center gap-2">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                    <span className="text-white text-sm font-medium">Welcome Email</span>
                    <span className="ml-auto text-violet-200 text-xs">Auto-sent</span>
                </div>
                <div className="p-5">
                    <div className="text-xs text-gray-400 mb-1">To: sarah@example.com</div>
                    <div className="text-sm font-semibold text-gray-900 mb-3">You&apos;re on the waitlist! 🎉</div>
                    <div className="bg-violet-50 rounded-xl p-3 text-center mb-3">
                        <div className="text-xs text-violet-600 font-semibold uppercase tracking-wide">Your Position</div>
                        <div className="text-3xl font-extrabold text-violet-700">#42</div>
                        <div className="text-xs text-violet-500">out of 128 on the waitlist</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2.5 mb-3">
                        <div className="text-xs text-gray-500 mb-1">🚀 Share to move up:</div>
                        <div className="text-xs text-violet-600 font-mono truncate">myapp.waitlistly.live?ref=a3k9x</div>
                    </div>
                </div>
            </div>

            {/* Bottom cards */}
            <div className="grid grid-cols-2 gap-3">
                {/* Owner notification */}
                <div className="bg-white rounded-xl shadow-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-emerald-100 rounded-md flex items-center justify-center">
                            <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                            </svg>
                        </div>
                        <span className="text-xs font-semibold text-gray-800">New Signup!</span>
                    </div>
                    <div className="text-[11px] text-gray-500">sarah@example.com just joined</div>
                    <div className="text-[11px] text-emerald-600 font-medium mt-1">📊 Total: 128 leads</div>
                </div>

                {/* Launch email */}
                <div className="bg-white rounded-xl shadow-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-orange-100 rounded-md flex items-center justify-center">
                            <svg className="w-3.5 h-3.5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                            </svg>
                        </div>
                        <span className="text-xs font-semibold text-gray-800">Launch Day</span>
                    </div>
                    <div className="text-[11px] text-gray-500">One-click blast to all</div>
                    <div className="text-[11px] text-orange-600 font-medium mt-1">🚀 128 emails sent!</div>
                </div>
            </div>
        </div>
    );
}

function WebhookVisual() {
    return (
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6 animate-fade-in relative overflow-hidden">
            {/* Background connection lines */}
            <div className="absolute inset-0 z-0 pointer-events-none opacity-30">
                <svg className="w-full h-full" viewBox="0 0 400 300">
                    <path d="M100 80 C 150 80, 150 150, 200 150 S 250 220, 300 220" stroke="#8B5CF6" strokeWidth="2" fill="none" strokeDasharray="5,5" className="animate-[dash_20s_linear_infinite]" />
                    <path d="M100 80 C 150 80, 150 150, 200 150 S 250 80, 300 80" stroke="#8B5CF6" strokeWidth="2" fill="none" strokeDasharray="5,5" className="animate-[dash_15s_linear_infinite]" />
                </svg>
            </div>

            <div className="relative z-10 flex flex-col items-center gap-8">
                {/* Source: Waitlistly */}
                <div className="bg-violet-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-lg shadow-violet-200">
                    <span className="font-bold text-lg">W</span>
                    <span className="font-medium text-sm">Waitlistly</span>
                </div>

                {/* Connection Badge */}
                <div className="bg-white border border-gray-200 text-gray-500 px-3 py-1 rounded-full text-xs font-mono shadow-sm flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    POST /webhook
                </div>

                {/* Destinations: Real logos (logos already contain text) */}
                <div className="grid grid-cols-2 gap-3 w-full">
                    <div className="bg-orange-50 border border-orange-200/60 rounded-xl p-3 flex items-center justify-center">
                        <img src="/assets/third-party-logos/Zapier_logo.jpg" alt="Zapier" className="h-8 rounded-lg object-contain" />
                    </div>

                    <div className="bg-purple-50 border border-purple-200/60 rounded-xl p-3 flex items-center justify-center">
                        <img src="/assets/third-party-logos/make.jpg" alt="Make" className="h-8 rounded-lg object-contain" />
                    </div>

                    <div className="bg-pink-50 border border-pink-200/60 rounded-xl p-3 flex items-center justify-center">
                        <img src="/assets/third-party-logos/slack-logo.jpg" alt="Slack" className="h-8 rounded-lg object-contain" />
                    </div>

                    <div className="bg-rose-50 border border-rose-200/60 rounded-xl p-3 flex items-center justify-center">
                        <img src="/assets/third-party-logos/n8n.png" alt="n8n" className="h-8 rounded-lg object-contain" />
                    </div>
                </div>
            </div>
        </div>
    );
}
